import { useEffect, useRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useTelegramBackButton } from '../../hooks/useTelegramBackButton';


interface FullScreenModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  /** If true, registers Telegram native BackButton handler while modal is open */
  showBackButton?: boolean;
  /** Optional custom handler for Telegram native BackButton */
  onBack?: () => void;
  /** Padding for the scrollable body content (NOT the full-screen container) */
  padding?: string;
  background?: string;
  borderRadius?: string;
  zIndex?: number;
}

export default function FullScreenModal({
  isOpen,
  onClose,
  title,
  children,
  showBackButton = false,
  onBack,
  padding = '0 20px 32px 20px',
  background = 'var(--color-fullscreen-surface)',
  borderRadius = '0px',
  zIndex = 3000,
}: FullScreenModalProps) {
  const { t } = useTranslation();
  const openTimeRef = useRef<number>(0);

  useTelegramBackButton(isOpen && showBackButton, () => {
    if (onBack) {
      onBack();
    } else {
      onClose();
    }
  });

  useEffect(() => {
    if (isOpen) {
      openTimeRef.current = Date.now();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const node = (
    <div
      className="fixed inset-0 flex h-dvh items-stretch justify-center bg-black"
      style={{ zIndex }}
      onClick={() => {
        if (Date.now() - openTimeRef.current < 100) {
          return;
        }
        onClose();
      }}
    >
      <div
        className="relative box-border h-dvh w-full overflow-y-auto text-white shadow-none overscroll-contain"
        style={{
          background,
          borderRadius,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header (matches ModalFrame behavior, but full-screen) */}
        <header
          className="sticky top-0 mb-xl flex items-center justify-between bg-[linear-gradient(180deg,var(--color-fullscreen-surface)_0%,var(--color-fullscreen-surface-alpha-70)_50%,var(--color-fullscreen-surface-alpha-0)_100%)] px-2xl pt-2xl pb-xl"
          style={{ zIndex: zIndex + 10 }}
        >
          <div className="h-9 w-9 shrink-0" />

          {title ? (
            <div className="flex flex-1 items-center justify-center text-center">
              <p className="m-0 max-w-[280px] [font-family:Lato,sans-serif] text-[18px] font-bold text-white">
                {title}
              </p>
            </div>
          ) : (
            <div className="flex-1" />
          )}

          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="m-0 h-9 w-9 shrink-0 cursor-pointer border-none bg-transparent p-0 text-center text-[24px] leading-9 text-white"
            aria-label={t('Close')}
          >
            ×
          </button>
        </header>

        <div style={{ padding }}>{children}</div>
      </div>
    </div>
  );

  // Portal to <body> to avoid stacking-context issues (e.g. bottom nav / transformed parents)
  return typeof document !== 'undefined' ? createPortal(node, document.body) : node;
}
